/**
 * usePatientClinicalMemory
 *
 * React hook that loads and exposes a patient's cached clinical memory.
 *
 *   const { memory, loading, error, isStale, pollingGaveUp, refresh } =
 *     usePatientClinicalMemory(patientId, practiceId);
 *
 * - Calls GET /api/clinical-memory/:patientId on mount and whenever the
 *   identifiers change.
 * - While the document is INDEXING / STALE it polls the cheap
 *   /status endpoint — NOT the full document — and re-fetches the document
 *   once, when the status settles to INDEXED / FAILED / NOT_INDEXED.
 * - `refresh()` POSTs to /api/clinical-memory/:patientId/refresh and starts
 *   polling. Resolves immediately — the actual rebuild happens in the worker.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Why the polling is shaped the way it is
 *
 * This hook used to re-fetch the WHOLE memory document every 4 seconds in
 * order to read one string off it. The document carries `timeline`, which is
 * one AI-generated summary per session (600 tokens each) and grows without
 * bound, so a long-history patient is a several-hundred-kilobyte response.
 * At 15 polls a minute that is megabytes per minute, per open panel, and it
 * showed up as tens of megabytes of Azure egress for a single refresh.
 *
 * Three things keep it bounded now:
 *
 *   1. Poll `/status` (tens of bytes), not the document. The document is
 *      fetched once up front and once more when the rebuild settles.
 *   2. Back off — 4s, then 6s, 9s … capped at 30s. A rebuild that finishes
 *      quickly is still observed quickly; one that drags stops costing a
 *      request every 4 seconds.
 *   3. Give up after MAX_POLL_MS. `STALE` and `INDEXING` are only *usually*
 *      transient: if the worker is down, or a job dies mid-build, the document
 *      sits in one of them indefinitely and the old loop would poll a dead
 *      state for as long as the tab stayed open. `pollingGaveUp` lets the UI
 *      offer "check again" instead of spinning forever.
 *
 * And polling pauses entirely while the tab is hidden, which is where a
 * forgotten tab used to quietly accumulate traffic all night.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPatientClinicalMemory,
  getPatientClinicalMemoryStatus,
  refreshPatientClinicalMemory,
} from "../api";

const POLL_START_MS = 4000;
const POLL_MAX_MS = 30000;
const POLL_BACKOFF = 1.5;
/** Stop polling a status that will not settle. ~5 minutes. */
const MAX_POLL_MS = 5 * 60 * 1000;

const ACTIVELY_BUILDING_STATUSES = new Set(["INDEXING", "STALE"]);

export function usePatientClinicalMemory(patientId, practiceId) {
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pollingGaveUp, setPollingGaveUp] = useState(false);

  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);
  // Poll bookkeeping, in refs rather than state: changing these must never
  // re-render, and the visibility listener needs to read them without being
  // re-registered on every tick.
  const delayRef = useRef(POLL_START_MS);
  const startedAtRef = useRef(0);
  const pollingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pollingRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  /** Fetch the FULL document. Used on mount and once more when a build ends. */
  const fetchOnce = useCallback(
    async ({ silent = false } = {}) => {
      if (!patientId) return null;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const data = await getPatientClinicalMemory(patientId, practiceId);
        if (!mountedRef.current) return data;
        setMemory(data);
        return data;
      } catch (err) {
        if (!mountedRef.current) return null;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load clinical memory";
        setError(msg);
        return null;
      } finally {
        if (mountedRef.current && !silent) setLoading(false);
      }
    },
    [patientId, practiceId],
  );

  const stopPolling = useCallback(() => {
    pollingRef.current = false;
    clearTimer();
  }, [clearTimer]);

  // Declared as a ref-held function because schedulePoll and the visibility
  // listener are mutually recursive: the tick reschedules itself, and the
  // listener restarts a tick. A plain useCallback pair would need one of them
  // in the other's dependency array and re-create the listener every tick.
  const tickRef = useRef(null);

  const schedulePoll = useCallback(
    (delay) => {
      clearTimer();
      if (!pollingRef.current) return;
      pollTimerRef.current = setTimeout(() => tickRef.current?.(), delay);
    },
    [clearTimer],
  );

  tickRef.current = async () => {
    if (!mountedRef.current || !pollingRef.current) return;

    // Hidden tab: hold, don't poll. The visibilitychange listener resumes.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }

    if (Date.now() - startedAtRef.current > MAX_POLL_MS) {
      stopPolling();
      if (mountedRef.current) setPollingGaveUp(true);
      return;
    }

    let status = null;
    try {
      status = await getPatientClinicalMemoryStatus(patientId, practiceId);
    } catch {
      // A failed status check is not a failed build — the rebuild is happening
      // server-side regardless. Stay in the loop and let the backoff and the
      // MAX_POLL_MS ceiling handle a backend that is genuinely unreachable.
    }
    if (!mountedRef.current || !pollingRef.current) return;

    if (status && !ACTIVELY_BUILDING_STATUSES.has(status.indexStatus)) {
      // Settled. Pull the full document exactly once.
      stopPolling();
      await fetchOnce({ silent: true });
      return;
    }

    delayRef.current = Math.min(
      Math.round(delayRef.current * POLL_BACKOFF),
      POLL_MAX_MS,
    );
    schedulePoll(delayRef.current);
  };

  const startPolling = useCallback(() => {
    pollingRef.current = true;
    delayRef.current = POLL_START_MS;
    startedAtRef.current = Date.now();
    setPollingGaveUp(false);
    schedulePoll(delayRef.current);
  }, [schedulePoll]);

  // Resume a held poll as soon as the tab comes back.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (!pollingRef.current || pollTimerRef.current) return;
      schedulePoll(0);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [schedulePoll]);

  // Initial load + auto-poll when STALE / INDEXING
  useEffect(() => {
    if (!patientId) {
      stopPolling();
      setMemory(null);
      setError(null);
      setLoading(false);
      setPollingGaveUp(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const data = await fetchOnce();
      if (cancelled || !mountedRef.current) return;
      if (data && ACTIVELY_BUILDING_STATUSES.has(data.indexStatus)) {
        startPolling();
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [patientId, practiceId, fetchOnce, startPolling, stopPolling]);

  const refresh = useCallback(async () => {
    if (!patientId) return;
    try {
      await refreshPatientClinicalMemory(patientId, practiceId);
      // Reflect STALE immediately, then watch /status until it settles.
      await fetchOnce({ silent: true });
      startPolling();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to queue refresh";
      setError(msg);
    }
  }, [patientId, practiceId, fetchOnce, startPolling]);

  const isStale =
    !!memory && ACTIVELY_BUILDING_STATUSES.has(memory.indexStatus);

  return { memory, loading, error, isStale, pollingGaveUp, refresh };
}

export default usePatientClinicalMemory;
